function urlBase64ToUint8Array(base64String: string) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Check if the user is currently on any Chitra AI page (home or library)
 * Used to conditionally skip notifications when user is already viewing Chitra
 */
export function isOnChitraPage(): boolean {
  if (typeof window === 'undefined') return false;
  const pathname = window.location.pathname;
  return pathname.startsWith('/chitra');
}

export async function initNewsNotifications() {
  if (typeof window === 'undefined') return;
  if (!('serviceWorker' in navigator)) return;
  if (Notification.permission === 'denied') return;
  if (location.protocol !== 'https:' && location.hostname !== 'localhost') return;

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return;

  const reg = await navigator.serviceWorker.ready;

  const vapidPublicKey = (import.meta as any).env.VITE_VAPID_PUBLIC_KEY as string;
  if (!vapidPublicKey) {
    // console.warn('VAPID public key missing: set VITE_VAPID_PUBLIC_KEY');
    return;
  }

  let subscription = await reg.pushManager.getSubscription();
  if (!subscription) {
    subscription = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
    });
  }

  const supabaseUrl = 'https://arikhtozvkvxlraiuenh.supabase.co';
  const edgeUrl = `${supabaseUrl}/functions/v1/web-push-send`;

  // Send a test push and allow backend to optionally store the subscription
  await fetch(edgeUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      // Optional: supply anon key if the function verifies JWTs
      'apikey': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImFyaWtodG96dmt2eGxyYWl1ZW5oIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTA1MTUzNDYsImV4cCI6MjA2NjA5MTM0Nn0.dlbYO0MDYeUENh9tYSJb7ctfxGFEhyahDJ4Ia00WCNU',
      'Authorization': 'Bearer ' + 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImFyaWtodG96dmt2eGxyYWl1ZW5oIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTA1MTUzNDYsImV4cCI6MjA2NjA5MTM0Nn0.dlbYO0MDYeUENh9tYSJb7ctfxGFEhyahDJ4Ia00WCNU',
    },
    body: JSON.stringify({
      subscription,
      payload: {
        title: 'Chitra AI',
        body: 'You will receive background notifications for your creations.',
        url: '/chitra/library',
        silent: false,
      },
    }),
  }).catch(() => void 0);
}

export async function sendLocalNotification(title: string, options?: NotificationOptions) {
  if (typeof window === 'undefined' || !('Notification' in window)) return;

  // Add vibration to options if not present
  const notificationOptions = {
    vibrate: [200, 100, 200],
    ...options
  } as NotificationOptions & { vibrate?: number[] };

  // Helper to trigger vibration
  const triggerVibration = () => {
    if ('vibrate' in navigator) {
      try {
        navigator.vibrate([200, 100, 200]);
      } catch (e) {
        console.warn('Navigator vibration failed', e);
      }
    }
  };

  if (Notification.permission === 'granted') {
    // Try to get service worker registration first for background support if needed
    try {
      if ('serviceWorker' in navigator) {
        const reg = await navigator.serviceWorker.ready;
        // Trigger vibration at the same time as notification
        triggerVibration();
        reg.showNotification(title, notificationOptions);
        return;
      }
    } catch (e) {
      console.warn('Service worker notification failed, falling back to window Notification', e);
    }

    // Fallback to basic window Notification
    triggerVibration();
    new Notification(title, notificationOptions);
  } else if (Notification.permission === 'default') {
    const permission = await Notification.requestPermission();
    if (permission === 'granted') {
      triggerVibration();
      new Notification(title, notificationOptions);
    }
  } else {
    console.warn('[Notification] Permission denied');
  }
}

